import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [attempts, setAttempts] = useState(0);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;
    const poll = async (n) => {
      if (!active) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setDetails(data); setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* ignore */ }
      if (n >= 12) { setState("failed"); return; }
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => { active = false; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 p-12 text-center shadow-xl" data-testid="payment-result">
        {state === "checking" && (
          <>
            <Spinner size={48} className="animate-spin mx-auto text-slate-400" />
            <h1 className="font-display text-2xl mt-6 tracking-tight">Confirming payment…</h1>
            <p className="text-sm text-slate-500 mt-2">Hang tight while we verify with Stripe. ({attempts})</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle size={56} weight="fill" className="mx-auto text-emerald-600" />
            <h1 className="font-display text-3xl mt-6 tracking-tight">Payment Confirmed</h1>
            <p className="text-slate-500 mt-3">
              {details?.type === "ai" ? "Your AI service package is confirmed. Our team will reach out to onboard you." : "Your slots are now reserved and your offer is live. Thank you for partnering with PMI."}
            </p>
            {details?.type === "merchant_zone" && details?.creative_token && (
              <div className="mt-6 border border-amber-300 bg-amber-50 p-5 text-left" data-testid="creative-cta">
                <p className="text-sm font-[700] text-navy-900">Add your ad artwork</p>
                <p className="text-xs text-slate-600 mt-1">Upload the creative that will print on your postcard block. We've also emailed this secure link to you.</p>
                <Link to={`/creative/${details.creative_token}`} data-testid="creative-link"
                  className="inline-block mt-3 bg-amber-600 text-white px-6 py-2.5 text-sm font-[600] hover:bg-amber-700 transition-colors">
                  Upload My Creative
                </Link>
              </div>
            )}
            {details && (
              <div className="mt-6 border border-slate-200 divide-y divide-slate-100 text-left" data-testid="receipt">
                {details.type === "slots" && (
                  <div className="flex justify-between px-4 py-3 text-sm">
                    <span className="text-slate-500">Slots reserved</span>
                    <span className="font-[600]" data-testid="receipt-slots">{details.slot_count}</span>
                  </div>
                )}
                {details.type === "slots" && (
                  <div className="flex justify-between px-4 py-3 text-sm">
                    <span className="text-slate-500">Bundle discount applied</span>
                    <span className={`font-[600] ${details.discount_pct > 0 ? "text-emerald-700" : "text-slate-400"}`} data-testid="receipt-discount">
                      {details.discount_pct > 0 ? `${details.discount_pct}% off` : "None"}
                    </span>
                  </div>
                )}
                {details.type === "ai" && details.monthly_total > 0 && (
                  <>
                    <div className="flex justify-between px-4 py-3 text-sm">
                      <span className="text-slate-500">Setup (one-time)</span>
                      <span className="font-[600]" data-testid="receipt-setup">${Number(details.amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between px-4 py-3 text-sm">
                      <span className="text-slate-500">First month</span>
                      <span className="font-[600]">${Number(details.monthly_total || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between px-4 py-3 text-sm">
                      <span className="text-slate-500">Recurring</span>
                      <span className="font-[600] text-navy-900" data-testid="receipt-recurring">${Number(details.monthly_total || 0).toFixed(2)}/mo</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between px-4 py-3">
                  <span className="text-sm uppercase tracking-wider font-bold text-slate-500 self-center">{details.type === "ai" && details.monthly_total > 0 ? "Charged today" : "Total charged"}</span>
                  <span className="font-display text-2xl text-navy-900" data-testid="receipt-total">${(Number(details.amount || 0) + (details.type === "ai" ? Number(details.monthly_total || 0) : 0)).toFixed(2)}</span>
                </div>
              </div>
            )}
            <Link to="/partner" className="inline-block mt-8 bg-navy-900 text-white px-8 py-3 font-[600] hover:bg-emerald-700 transition-colors" data-testid="back-to-portal">
              Back to Portal
            </Link>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle size={56} weight="fill" className="mx-auto text-red-500" />
            <h1 className="font-display text-3xl mt-6 tracking-tight">Payment Not Completed</h1>
            <p className="text-slate-500 mt-3">We couldn't confirm your payment. No slots were reserved.</p>
            <Link to="/partner" className="inline-block mt-8 border border-slate-300 px-8 py-3 font-[600] hover:border-navy-900 transition-colors">
              Return to Portal
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 p-12 text-center shadow-xl">
        <XCircle size={56} weight="fill" className="mx-auto text-slate-400" />
        <h1 className="font-display text-3xl mt-6 tracking-tight">Checkout Cancelled</h1>
        <p className="text-slate-500 mt-3">No charge was made. Your selected slots are still available.</p>
        <Link to="/partner" className="inline-block mt-8 bg-navy-900 text-white px-8 py-3 font-[600] hover:bg-emerald-700 transition-colors">
          Back to Portal
        </Link>
      </div>
    </div>
  );
}
